import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api'; 
import { useAuth } from '../contexts/AuthContext'; 
import { useDirtyForm } from '../contexts/DirtyFormContext';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

const getImportMetaEnv = () => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env;
    }
  } catch (error) {
    // ignore
  }
  return undefined;
};

const resolvePublicPath = () => {
  const metaEnv = getImportMetaEnv();
  if (metaEnv?.VITE_PUBLIC_URL) {
    return metaEnv.VITE_PUBLIC_URL;
  }

  if (metaEnv?.BASE_URL) {
    return metaEnv.BASE_URL;
  }

  const publicUrl = process.env.PUBLIC_URL;
  if (publicUrl) {
    return publicUrl;
  }

  return '';
};

const buildWorkerSrc = () => {
  const publicPath = resolvePublicPath();
  const normalizedPath =
    typeof publicPath === 'string'
      ? publicPath.endsWith('/')
        ? publicPath.slice(0, -1)
        : publicPath
      : '';

  if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
    return `${normalizedPath}/pdf.worker.min.js`;
  }

  const origin =
    typeof window !== 'undefined' && window.location && window.location.origin
      ? window.location.origin
      : '';
  const base = normalizedPath ? `${origin}${normalizedPath.startsWith('/') ? '' : '/'}${normalizedPath}` : origin;
  return `${base}/pdf.worker.min.js`;
};

pdfjs.GlobalWorkerOptions.workerSrc = buildWorkerSrc();


const baseStyles = {
  filtersContainer: { 
    background: '#f8f9fa', 
    padding: '15px', 
    borderRadius: '5px',
    position: 'relative', 
    top: 0, 
    zIndex: 10, 
    borderBottom: '1px solid #dee2e6'
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  filterLabel: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: '#555',
    paddingLeft: '2px',
  },
  select: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  searchInput: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '1400px', borderCollapse: 'collapse', tableLayout: 'fixed' }, 
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', fontSize: '0.9rem', whiteSpace: 'nowrap', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem', textAlign: 'center', wordBreak: 'break-word' },
  button: { padding: '5px 10px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
  approveButton: { backgroundColor: '#28a745', color: 'white' },
  rejectButton: { backgroundColor: '#dc3545', color: 'white' },
  cancelButton: { backgroundColor: '#ffc107', color: 'black' },
  cancelRequestApproveButton: { backgroundColor: '#fd7e14', color: 'white' },
  cancelRequestRejectButton: { backgroundColor: '#6c757d', color: 'white' },
  modalButton: { backgroundColor: '#17a2b8', color: 'white' },
  countDisplay: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    paddingLeft: '5px' 
  },
  filterFooter: {
    marginTop: '15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    padding: '20px',
    borderRadius: '10px',
    width: '80vw',
    maxWidth: '1000px',
    maxHeight: '85vh',
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #ddd'
  },
  modalBody: {
    marginBottom: '20px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  filePreviewContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#f6f7f9',
    borderRadius: '6px',
    border: '1px solid #e1e5eb',
    overflow: 'auto'
  },
  filePreview: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    border: 'none',
    objectFit: 'contain',
    objectPosition: 'top left'
  },
  pdfObject: {
    width: '100%',
    height: '100%'
  },
  pdfControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    gap: '12px'
  },
  pdfControlsLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  pdfControlsRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  pdfButton: {
    padding: '6px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    background: '#fff',
    cursor: 'pointer'
  },
  pdfDisabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  excelButton: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '0.95rem'
  },
  pdfPageInfo: {
    fontSize: '0.9rem',
    color: '#555'
  },
  actionButtons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center'
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold'
  },
  statusPending: {
    backgroundColor: '#fff3cd',
    color: '#856404'
  },
  statusApproved: {
    backgroundColor: '#d4edda',
    color: '#155724'
  },
  statusRejected: {
    backgroundColor: '#f8d7da',
    color: '#721c24'
  }
};

const LeaveApprovalPage = () => {
  const { user } = useAuth(); 
  const { setIsDirty } = useDirtyForm();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('전체');
  const [selectedStatus, setSelectedStatus] = useState('접수중');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [searchDistrict, setSearchDistrict] = useState('');
  // 실시간 입력값
  const [searchName, setSearchName] = useState('');
  // 검색 버튼 눌렀을 때만 반영되는 조회 조건
  const [appliedSearchName, setAppliedSearchName] = useState('');
  const [appliedYear, setAppliedYear] = useState(null);
  const [appliedMonth, setAppliedMonth] = useState(null);
  const [appliedStatus, setAppliedStatus] = useState(null);
  const [appliedProjectId, setAppliedProjectId] = useState(null);
  const [appliedSearchDistrict, setAppliedSearchDistrict] = useState('');
  const [applications, setApplications] = useState([]);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFileName, setPreviewFileName] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewObjectUrl, setPreviewObjectUrl] = useState('');
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [previewPdfData, setPreviewPdfData] = useState(null);
  const [previewPdfTotalPages, setPreviewPdfTotalPages] = useState(0);
  const [previewPdfPage, setPreviewPdfPage] = useState(1);
  const [previewPdfScale, setPreviewPdfScale] = useState(1);
  const [exportLoading, setExportLoading] = useState(false);
  const previewUrlRef = useRef('');
  const previewContainerRef = useRef(null);
  const [previewContainerWidth, setPreviewContainerWidth] = useState(0);
  const [previewTargetApp, setPreviewTargetApp] = useState(null);

  // 연차 승인 설정 (관리자)
  const [approvalMode, setApprovalMode] = useState('manual');
  const [autoApproverId, setAutoApproverId] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saveSettingsLoading, setSaveSettingsLoading] = useState(false);
  const [isConfirmSettingsModalOpen, setIsConfirmSettingsModalOpen] = useState(false);
  const [confirmSettingsPending, setConfirmSettingsPending] = useState(null); // { approvalMode, autoApproverId } to apply on confirm

  // 반려 모달
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectTargetApp, setRejectTargetApp] = useState(null);
  const [rejectComment, setRejectComment] = useState('');

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
  }, []);

  const fetchPreviewFile = useCallback(async (filename) => {
    if (!filename) return;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.get(`/leave/applications/files/${encodeURIComponent(filename)}`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      revokePreviewUrl();

      const blob = response.data;
      const isPdf = filename.toLowerCase().endsWith('.pdf');

      if (isPdf) {
        setPreviewPdfData(blob);
        setPreviewPdfTotalPages(0);
        setPreviewPdfPage(1);
        setPreviewPdfScale(1);
        setPreviewObjectUrl('');
        previewUrlRef.current = '';
      } else {
        const objectUrl = URL.createObjectURL(blob);
        previewUrlRef.current = objectUrl;
        setPreviewObjectUrl(objectUrl);
        setPreviewPdfData(null);
        setPreviewPdfTotalPages(0);
      }
    } catch (error) {
      console.error('Preview file load error:', error);
      setPreviewError(error.response?.data?.message || '파일을 불러오는 중 오류가 발생했습니다.');
      setPreviewObjectUrl('');
      setPreviewPdfData(null);
      setPreviewPdfTotalPages(0);
    } finally {
      setPreviewLoading(false);
    }
  }, [revokePreviewUrl]);

  useEffect(() => {
    if (isPreviewModalOpen && previewFileName) {
      fetchPreviewFile(previewFileName);
    }
  }, [isPreviewModalOpen, previewFileName, fetchPreviewFile]);

  useEffect(() => {
    return () => {
      revokePreviewUrl();
    };
  }, [revokePreviewUrl]);

  useEffect(() => {
    if (!isPreviewModalOpen || !previewContainerRef.current) {
      return;
    }

    setPreviewContainerWidth(previewContainerRef.current.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setPreviewContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(previewContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isPreviewModalOpen]);

  const previewModalStyle = {
    ...baseStyles.modal,
    width: isPreviewFullscreen ? '95vw' : '80vw',
    maxWidth: isPreviewFullscreen ? '1400px' : '1000px',
    maxHeight: isPreviewFullscreen ? '95vh' : '85vh',
    minHeight: isPreviewFullscreen ? '85vh' : '60vh',
    overflow: 'hidden'
  };

  const previewBodyStyle = {
    ...baseStyles.modalBody,
    overflow: 'auto'
  };

  const isPdfPreview = previewFileName?.toLowerCase().endsWith('.pdf');
  const basePdfWidth = Math.max(previewContainerWidth, 320);
  const effectivePdfWidth = basePdfWidth * previewPdfScale;
  const canPdfPrev = previewPdfPage > 1;
  const canPdfNext = previewPdfPage < previewPdfTotalPages;
  const pdfButtonStyle = (disabled = false) =>
    disabled ? { ...baseStyles.pdfButton, ...baseStyles.pdfDisabledButton } : baseStyles.pdfButton;

  const handlePdfPageChange = (delta) => {
    setPreviewPdfPage((prev) => {
      const next = prev + delta;
      if (next < 1 || next > previewPdfTotalPages) {
        return prev;
      }
      return next;
    });
  };

  const handlePdfZoom = (delta) => {
    setPreviewPdfScale((prev) => {
      const next = Math.min(2.5, Math.max(0.6, prev + delta));
      return Number(next.toFixed(2));
    });
  };

  const handlePdfDocumentLoad = ({ numPages }) => {
    setPreviewPdfTotalPages(numPages || 1);
    setPreviewPdfPage(1);
  };

  const handlePdfDocumentError = (error) => {
    console.error('PDF preview load error:', error);
    setPreviewError(error?.message || 'PDF 파일을 불러오는 중 오류가 발생했습니다.');
  };

  const previewContainerStyle = {
    ...baseStyles.filePreviewContainer,
    minHeight: isPreviewFullscreen ? '0' : '60vh',
    display: isPdfPreview ? 'block' : baseStyles.filePreviewContainer.display,
    overflow: 'auto',
    height: '100%',
    ...(isPdfPreview ? {} : { alignItems: 'flex-start', justifyContent: 'flex-start' })
  };

  // 월 옵션 생성
  const monthOptions = ['전체', ...Array.from({length: 12}, (_, i) => i + 1)];
  
  // 상태 옵션
  const statusOptions = ['전체', '접수중', '승인', '반려'];

  // 연차 신청 목록 조회 (overrideParams 전달 시 해당 조건으로, 없으면 마지막 적용된 조건으로)
  const fetchApplications = useCallback(async (overrideParams) => {
    const year = overrideParams?.year ?? appliedYear ?? selectedYear;
    if (!year) return;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const params = {
        year,
        status: (overrideParams?.status ?? appliedStatus ?? selectedStatus) === '전체' ? undefined : (overrideParams?.status ?? appliedStatus ?? selectedStatus)
      };

      const month = overrideParams?.month ?? appliedMonth ?? selectedMonth;
      if (month && month !== '전체') {
        params.month = parseInt(month, 10);
      }

      const projectId = overrideParams?.projectId ?? appliedProjectId ?? selectedProject;
      if (projectId) {
        params.projectId = projectId;
      }

      const district = (overrideParams?.district ?? appliedSearchDistrict ?? searchDistrict)?.trim?.() ?? '';
      if (district) {
        params.district = district;
      }

      const participantName = (overrideParams?.participantName ?? appliedSearchName ?? searchName)?.trim?.() ?? '';
      if (participantName) {
        params.participantName = participantName;
      }

      const cancelParams = { year: params.year };
      if (params.month) cancelParams.month = params.month;
      if (params.projectId) cancelParams.projectId = params.projectId;

      const [appsRes, cancelRes] = await Promise.all([
        api.get('/leave/applications', { params }),
        api.get('/leave/cancellation-requests', { params: cancelParams })
      ]);
      setApplications(appsRes.data);
      setCancellationRequests(cancelRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '연차 신청 목록 로딩 실패');
      setApplications([]);
      setCancellationRequests([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedStatus, selectedProject, searchDistrict, searchName, appliedYear, appliedMonth, appliedStatus, appliedProjectId, appliedSearchDistrict, appliedSearchName]);

  const handleSearchClick = () => {
    setAppliedYear(selectedYear);
    setAppliedMonth(selectedMonth);
    setAppliedStatus(selectedStatus);
    setAppliedProjectId(selectedProject);
    setAppliedSearchDistrict(searchDistrict);
    setAppliedSearchName(searchName);
    fetchApplications({
      year: selectedYear,
      month: selectedMonth,
      status: selectedStatus,
      projectId: selectedProject,
      district: searchDistrict,
      participantName: searchName
    });
  };

  useEffect(() => {
    if (!selectedYear) {
      setProjects([]);
      return;
    }
    let ignore = false;
    const loadProjects = async () => {
      try {
        const res = await api.get('/projects', { params: { year: selectedYear } });
        if (!ignore) {
          setProjects(res.data || []);
        }
      } catch (err) {
        if (!ignore) {
          setProjects([]);
        }
      }
    };
    loadProjects();
    return () => {
      ignore = true;
    };
  }, [selectedYear]);

  // 연차 승인 설정 로드 (관리자만)
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let ignore = false;
    const load = async () => {
      setSettingsLoading(true);
      try {
        const [settingsRes, adminsRes] = await Promise.all([
          api.get('/leave/applications/settings/approval'),
          api.get('/leave/applications/admins')
        ]);
        if (!ignore) {
          setApprovalMode(settingsRes.data?.approvalMode || 'manual');
          setAutoApproverId(settingsRes.data?.autoApproverId != null ? String(settingsRes.data.autoApproverId) : '');
          setAdminUsers(adminsRes.data || []);
        }
      } catch (err) {
        if (!ignore) {
          setAdminUsers([]);
        }
      } finally {
        if (!ignore) setSettingsLoading(false);
      }
    };
    load();
    return () => { ignore = true; };
  }, [user?.role]);

  const handleSaveApprovalSettingsClick = () => {
    const mode = approvalMode === 'auto' ? 'auto' : 'manual';
    if (mode === 'auto' && !autoApproverId) {
      setError('자동승인을 사용할 경우 자동승인자를 선택해 주세요.');
      return;
    }
    setConfirmSettingsPending({ approvalMode: mode, autoApproverId: mode === 'auto' ? autoApproverId : '' });
    setIsConfirmSettingsModalOpen(true);
  };

  const handleConfirmSettingsModalConfirm = async () => {
    if (!confirmSettingsPending) return;
    setSaveSettingsLoading(true);
    setError('');
    setMessage('');
    try {
      await api.put('/leave/applications/settings/approval', {
        approvalMode: confirmSettingsPending.approvalMode,
        autoApproverId: confirmSettingsPending.approvalMode === 'auto' ? confirmSettingsPending.autoApproverId : null
      });
      setMessage('승인 설정이 저장되었습니다.');
      setApprovalMode(confirmSettingsPending.approvalMode);
      setAutoApproverId(confirmSettingsPending.approvalMode === 'auto' ? confirmSettingsPending.autoApproverId : '');
    } catch (err) {
      setError(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaveSettingsLoading(false);
      setIsConfirmSettingsModalOpen(false);
      setConfirmSettingsPending(null);
    }
  };

  const handleConfirmSettingsModalCancel = () => {
    setIsConfirmSettingsModalOpen(false);
    setConfirmSettingsPending(null);
  };

  const handleStatusChange = async (applicationId, newStatus, comment = '') => {
    if (newStatus === '취소') {
      const confirmed = window.confirm('승인된 연차 신청을 취소하시겠습니까?');
      if (!confirmed) {
        return;
      }
    }
    try {
      await api.put(`/leave/applications/${applicationId}`, {
        status: newStatus,
        comment: comment
      });

      setMessage(`연차 신청이 ${newStatus}되었습니다.`);
      fetchApplications(); // 목록 새로고침
    } catch (err) {
      setError(err.response?.data?.message || `연차 신청 ${newStatus} 실패`);
    }
  };

  const handleOpenRejectModal = (app) => {
    setRejectTargetApp(app);
    setRejectComment('');
    setIsRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectTargetApp(null);
    setRejectComment('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectTargetApp) return;
    await handleStatusChange(rejectTargetApp.id, '반려', rejectComment.trim());
    handleCloseRejectModal();
  };

  const handleCancellationRequestApprove = async (cancelReq) => {
    if (!cancelReq?.id) return;
    try {
      await api.put(`/leave/cancellation-requests/${cancelReq.id}`, { status: '승인' });
      setMessage('승인취소 요청이 승인되었습니다.');
      fetchApplications();
    } catch (err) {
      setError(err.response?.data?.message || '승인 처리 실패');
    }
  };

  const handleCancellationRequestAcceptCancel = async (cancelReq) => {
    if (!cancelReq?.id) return;
    if (!window.confirm('승인취소 신청을 접수 취소하시겠습니까?')) return;
    try {
      await api.put(`/leave/cancellation-requests/${cancelReq.id}`, { status: '접수취소' });
      setMessage('승인취소 신청이 접수 취소되었습니다.');
      fetchApplications();
    } catch (err) {
      setError(err.response?.data?.message || '접수 취소 실패');
    }
  };

  const handleExcelExport = useCallback(async () => {
    if (exportLoading) {
      return;
    }

    setExportLoading(true);
    setMessage('');
    setError('');

    try {
      const params = {
        year: selectedYear
      };

      if (selectedStatus && selectedStatus !== '전체') {
        params.status = selectedStatus;
      }

      if (selectedMonth && selectedMonth !== '전체') {
        params.month = parseInt(selectedMonth, 10);
      }

      if (selectedProject) {
        params.projectId = selectedProject;
      }

      if (searchDistrict.trim()) {
        params.district = searchDistrict.trim();
      }

      if (searchName.trim()) {
        params.participantName = searchName.trim();
      }

      const response = await api.get('/leave/applications/export', {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthLabel = selectedMonth && selectedMonth !== '전체' ? `_${selectedMonth}` : '';
      link.download = `leave_applications_${selectedYear}${monthLabel}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage('엑셀 파일이 다운로드되었습니다.');
    } catch (err) {
      setError(err.response?.data?.message || '엑셀 출력에 실패했습니다.');
    } finally {
      setExportLoading(false);
    }
  }, [exportLoading, selectedYear, selectedStatus, selectedMonth, selectedProject, searchDistrict, searchName]);

  const handleFilePreview = (app) => {
    const files = app?.files;
    if (files && files.length > 0) {
      setPreviewTargetApp(app);
      setPreviewFiles(files);
      setPreviewFileName(files[0].filename);
      setIsPreviewFullscreen(false);
      setPreviewError('');
      setPreviewObjectUrl('');
      setPreviewPdfData(null);
      setPreviewPdfTotalPages(0);
      setPreviewPdfPage(1);
      setPreviewPdfScale(1);
      setIsPreviewModalOpen(true);
    }
  };

  const handleFileDownload = (filename, originalname) => {
    // API 서비스를 통해 파일 다운로드
    api.get(`/leave/applications/files/${filename}`, {
      responseType: 'blob'
    })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // 파일명이 한글인 경우 제대로 표시되도록 디코딩
      const decodedFilename = originalname ? decodeURIComponent(originalname) : filename;
      link.download = decodedFilename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch(error => {
      console.error('File download error:', error);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    });
  };

  const handlePreviewFileChange = (filename) => {
    if (!filename) return;
    setPreviewFileName(filename);
    setPreviewError('');
    setPreviewObjectUrl('');
    setPreviewPdfData(null);
    setPreviewPdfTotalPages(0);
    setPreviewPdfPage(1);
    setPreviewPdfScale(1);
  };

  const handleClosePreview = () => {
    revokePreviewUrl();
    setIsPreviewModalOpen(false);
    setPreviewTargetApp(null);
    setPreviewFileName(null);
    setPreviewFiles([]);
    setPreviewError('');
    setPreviewObjectUrl('');
    setIsPreviewFullscreen(false);
    setPreviewPdfData(null);
    setPreviewPdfTotalPages(0);
    setPreviewPdfPage(1);
    setPreviewPdfScale(1);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case '접수중':
        return baseStyles.statusPending;
      case '승인':
        return baseStyles.statusApproved;
      case '반려':
        return baseStyles.statusRejected;
      default:
        return baseStyles.statusBadge;
    }
  };

  const formatMonthDay = (ymd) => {
    if (!ymd) return '-';
    const parts = String(ymd).split('-').map((v) => parseInt(v, 10));
    const m = parts[1];
    const d = parts[2];
    if (!Number.isFinite(m) || !Number.isFinite(d)) return ymd;
    return `${m}/${d}`;
  };

  const getUsageDetails = (app) => {
    const details = app?.details;
    if (Array.isArray(details) && details.length > 0) {
      return details
        .map((d) => ({ date: d?.date, hours: parseFloat(d?.hours) || 0 }))
        .filter((d) => d.date && d.hours > 0);
    }
    return [{ date: app?.date, hours: parseFloat(app?.hours) || 0 }].filter((d) => d.date && d.hours > 0);
  };

  const getUsageTotalHours = (app) => {
    const details = getUsageDetails(app);
    return details.reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0);
  };

  const formatUsageHoursKo = (n) => {
    const x = parseFloat(n);
    if (!Number.isFinite(x)) return '0';
    if (Number.isInteger(x)) return String(x);
    const rounded = Math.round(x * 100) / 100;
    return String(rounded);
  };

  /** 예: 3시간(1일), 5시간(2일) */
  const formatUsageTimeCell = (app) => {
    const totalHours = getUsageTotalHours(app);
    const details = getUsageDetails(app);
    const dayCount = new Set(details.map((d) => d.date).filter(Boolean)).size;
    return `${formatUsageHoursKo(totalHours)}시간(${dayCount}일)`;
  };

  const formatUsageDateList = (app) => {
    const details = getUsageDetails(app);
    const sorted = [...details].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const dateList = [];
    const seen = new Set();
    for (const d of sorted) {
      const key = d.date;
      if (seen.has(key)) continue;
      seen.add(key);
      dateList.push(formatMonthDay(d.date));
    }
    return dateList.length ? dateList.join(', ') : '-';
  };

  return (
    <div>
      <h2>연차 승인 및 취소</h2>

      {/* 1. 필터 */}
      <div style={baseStyles.filtersContainer}>
        <div style={baseStyles.filterGroup}>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>연도</span>
            <input
              type="number"
              style={baseStyles.select} 
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              placeholder="연도 입력 (예: 2024)"
              min="2020"
              step="1"
            />
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>월</span>
            <select style={baseStyles.select} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              {monthOptions.map(m => <option key={m} value={m}>{m === '전체' ? m : `${m}월`}</option>)}
            </select>
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>상태</span>
            <select style={baseStyles.select} value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>사업단</span>
            <select
              style={baseStyles.select}
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="">전체</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.projectName}</option>
              ))}
            </select>
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>읍면동</span>
            <input
              type="text"
              style={baseStyles.searchInput}
              value={searchDistrict}
              onChange={(e) => setSearchDistrict(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchClick();
                }
              }}
              placeholder="읍면동 검색"
            />
          </div>
          <div style={baseStyles.filterItem}>
            <span style={baseStyles.filterLabel}>성명</span>
            <input
              type="text"
              style={baseStyles.searchInput}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchClick();
                }
              }}
              placeholder="성명 검색"
            />
          </div>
        </div>

        {/* 연차 승인 설정 (관리자만) */}
        {user?.role === 'admin' && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #cce5ff' }}>
            <div style={{ ...baseStyles.filterLabel, marginBottom: '8px' }}>승인 방식</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="approvalMode"
                  checked={approvalMode === 'manual'}
                  onChange={() => setApprovalMode('manual')}
                />
                <span>수동승인</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="approvalMode"
                  checked={approvalMode === 'auto'}
                  onChange={() => setApprovalMode('auto')}
                />
                <span>자동승인</span>
              </label>
              {approvalMode === 'auto' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={baseStyles.filterLabel}>자동승인자</span>
                  <select
                    style={baseStyles.select}
                    value={autoApproverId}
                    onChange={(e) => setAutoApproverId(e.target.value)}
                    disabled={settingsLoading}
                  >
                    <option value="">선택</option>
                    {adminUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.userId})</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="button"
                style={{ ...baseStyles.button, ...baseStyles.approveButton }}
                onClick={handleSaveApprovalSettingsClick}
                disabled={settingsLoading || saveSettingsLoading}
              >
                {saveSettingsLoading ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        )}

        {/* 인원 표시 및 검색/엑셀 출력 */}
        <div style={baseStyles.filterFooter}>
          <div style={baseStyles.countDisplay}>
            {loading ? '검색 중...' : `조회된 건수: 승인 신청 ${applications.length}건, 승인취소 신청 ${cancellationRequests.length}건`}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={{ ...baseStyles.button, backgroundColor: '#2563eb', color: '#fff' }}
              onClick={handleSearchClick}
              disabled={loading}
            >
              🔍 검색
            </button>
            <button
              style={{ ...baseStyles.button, ...baseStyles.excelButton }}
              onClick={handleExcelExport}
              disabled={loading || exportLoading || applications.length === 0}
            >
              {exportLoading ? '엑셀 생성 중...' : '엑셀 출력'}
            </button>
          </div>
        </div>
      </div>
      
      {message && <p style={{color: 'green', marginTop: '10px'}}>{message}</p>}
      {error && <p style={{color: 'red', marginTop: '10px'}}>{error}</p>}

      {/* 2-1. 승인 신청 테이블 */}
      <div style={{ marginTop: '20px' }}>
        <h3 style={{ marginBottom: '10px', color: '#2563eb' }}>승인 신청 ({applications.length}건)</h3>
        <div style={baseStyles.tableContainer}>
          {loading ? <p>로딩 중...</p> : (
            <table style={baseStyles.table}>
            <colgroup>
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '12%', minWidth: '150px' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '8%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={baseStyles.th}>신청일</th>
                <th style={baseStyles.th}>성명</th>
                <th style={baseStyles.th}>주민번호</th>
                <th style={baseStyles.th}>사업명</th>
                <th style={baseStyles.th}>사용일자</th>
                <th style={{ ...baseStyles.th, minWidth: 150, whiteSpace: 'nowrap' }}>사용시간</th>
                <th style={baseStyles.th}>의견</th>
                <th style={baseStyles.th}>상태</th>
                <th style={baseStyles.th}>승인자</th>
                <th style={baseStyles.th}>담당자</th>
                <th style={baseStyles.th}>첨부파일</th>
                <th style={baseStyles.th}>검토</th>
              </tr>
            </thead>
            <tbody>
              {applications.length > 0 ? applications.map(app => (
                <tr key={app.id}>
                  <td style={baseStyles.td}>{new Date(app.createdAt).toLocaleDateString()}</td>
                  <td style={baseStyles.td}>{app.participant?.name}</td>
                  <td style={baseStyles.td}>{app.participant?.rrn || '-'}</td>
                  <td style={baseStyles.td}>{app.project?.projectName}</td>
                  <td style={baseStyles.td}>{formatUsageDateList(app)}</td>
                  <td style={{ ...baseStyles.td, whiteSpace: 'nowrap' }}>{formatUsageTimeCell(app)}</td>
                  <td style={baseStyles.td}>{app.userComment || '-'}</td>
                  <td style={baseStyles.td}>
                    <span style={{...baseStyles.statusBadge, ...getStatusStyle(app.status)}}>
                      {app.status === '취소'
                        ? (app.comment === '접수 취소' ? '취소(접수)' : '취소(승인)')
                        : app.status}
                    </span>
                  </td>
                  <td style={baseStyles.td}>
                    {app.Approver?.name || '-'}
                  </td>
                  <td style={baseStyles.td}>{app.participant?.registrar || '-'}</td>
                  <td style={baseStyles.td}>
                    {app.files && app.files.length > 0 ? (
                      <button 
                        style={{...baseStyles.button, ...baseStyles.modalButton}}
                        onClick={() => handleFilePreview(app)}
                      >
                        파일보기 ({app.files.length})
                      </button>
                    ) : '-'}
                  </td>
                  <td style={baseStyles.td}>
                    <div style={baseStyles.actionButtons}>
                      {app.status === '접수중' && (
                        <>
                          <button 
                            style={{...baseStyles.button, ...baseStyles.approveButton}}
                            onClick={() => handleStatusChange(app.id, '승인')}
                            title="승인"
                          >
                            승인
                          </button>
                          <button 
                            style={{...baseStyles.button, ...baseStyles.rejectButton}}
                            onClick={() => handleOpenRejectModal(app)}
                            title="반려"
                          >
                            반려
                          </button>
                        </>
                      )}
                      {app.status === '승인' && (
                        <button 
                          style={{...baseStyles.button, ...baseStyles.cancelButton}}
                          onClick={() => handleStatusChange(app.id, '취소')}
                          title="취소"
                        >
                          취소
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="12" style={baseStyles.td}>검색된 연차 신청이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {/* 2-2. 승인취소 신청 테이블 */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ marginBottom: '10px', color: '#fd7e14' }}>승인취소 신청 ({cancellationRequests.length}건)</h3>
        <div style={baseStyles.tableContainer}>
          {loading ? <p>로딩 중...</p> : (
            <table style={baseStyles.table}>
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '13%', minWidth: '150px' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={baseStyles.th}>신청일</th>
                  <th style={baseStyles.th}>성명</th>
                  <th style={baseStyles.th}>주민번호</th>
                  <th style={baseStyles.th}>사업명</th>
                  <th style={baseStyles.th}>사용일자</th>
                  <th style={{ ...baseStyles.th, minWidth: 150, whiteSpace: 'nowrap' }}>사용시간</th>
                  <th style={baseStyles.th}>취소사유</th>
                  <th style={baseStyles.th}>담당자</th>
                  <th style={baseStyles.th}>첨부파일</th>
                  <th style={baseStyles.th}>검토</th>
                </tr>
              </thead>
              <tbody>
                {cancellationRequests.length > 0 ? cancellationRequests.map(cr => {
                  const app = cr.leaveApplication;
                  if (!app) return null;
                  return (
                    <tr key={cr.id}>
                      <td style={baseStyles.td}>{new Date(cr.createdAt).toLocaleDateString()}</td>
                      <td style={baseStyles.td}>{app.participant?.name}</td>
                      <td style={baseStyles.td}>{app.participant?.rrn || '-'}</td>
                      <td style={baseStyles.td}>{app.project?.projectName}</td>
                  <td style={baseStyles.td}>{formatUsageDateList(app)}</td>
                  <td style={{ ...baseStyles.td, whiteSpace: 'nowrap' }}>{formatUsageTimeCell(app)}</td>
                      <td style={baseStyles.td}>{cr.comment || '-'}</td>
                      <td style={baseStyles.td}>{app.participant?.registrar || '-'}</td>
                      <td style={baseStyles.td}>
                        {app.files && app.files.length > 0 ? (
                          <button
                            style={{ ...baseStyles.button, ...baseStyles.modalButton }}
                            onClick={() => handleFilePreview({ ...app, files: app.files })}
                          >
                            파일보기 ({app.files.length})
                          </button>
                        ) : '-'}
                      </td>
                      <td style={baseStyles.td}>
                        <div style={baseStyles.actionButtons}>
                          <button
                            style={{ ...baseStyles.button, ...baseStyles.cancelRequestApproveButton }}
                            onClick={() => handleCancellationRequestApprove(cr)}
                            title="승인 (연차 취소 처리)"
                          >
                            승인
                          </button>
                          <button
                            style={{ ...baseStyles.button, ...baseStyles.cancelRequestRejectButton }}
                            onClick={() => handleCancellationRequestAcceptCancel(cr)}
                            title="접수취소"
                          >
                            접수취소
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="10" style={baseStyles.td}>승인취소 신청이 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 파일 미리보기 모달 */}
      {isPreviewModalOpen && previewFileName && (
        <div style={baseStyles.modalOverlay}>
          <div style={previewModalStyle}>
            <div style={baseStyles.modalHeader}>
              <h3>파일 미리보기</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setIsPreviewFullscreen(prev => !prev)}
                  style={{ ...baseStyles.button, ...baseStyles.modalButton }}
                >
                  {isPreviewFullscreen ? '원래 크기' : '최대화'}
                </button>
                <button onClick={handleClosePreview} style={baseStyles.button}>×</button>
              </div>
            </div>
            <div style={previewBodyStyle}>
              {previewFiles.length > 1 && (
                <div style={{ marginBottom: '15px' }}>
                  <label>파일 선택: </label>
                  <select
                    onChange={(e) => handlePreviewFileChange(e.target.value)}
                    value={previewFileName || ''}
                    style={{ marginLeft: '10px', padding: '5px' }}
                  >
                    {previewFiles.map((file, index) => (
                      <option key={index} value={file.filename}>
                        {file.originalname}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {isPdfPreview && previewPdfData && (
                <div style={baseStyles.pdfControls}>
                  <div style={baseStyles.pdfControlsLeft}>
                    <button
                      onClick={() => handlePdfPageChange(-1)}
                      style={pdfButtonStyle(!canPdfPrev)}
                      disabled={!canPdfPrev}
                    >
                      이전
                    </button>
                    <span style={baseStyles.pdfPageInfo}>
                      {previewPdfPage} / {previewPdfTotalPages || 1}
                    </span>
                    <button
                      onClick={() => handlePdfPageChange(1)}
                      style={pdfButtonStyle(!canPdfNext)}
                      disabled={!canPdfNext}
                    >
                      다음
                    </button>
                  </div>
                  <div style={baseStyles.pdfControlsRight}>
                    <button
                      onClick={() => handlePdfZoom(-0.1)}
                      style={pdfButtonStyle(previewPdfScale <= 0.6)}
                      disabled={previewPdfScale <= 0.6}
                    >
                      축소
                    </button>
                    <span style={baseStyles.pdfPageInfo}>{Math.round(previewPdfScale * 100)}%</span>
                    <button
                      onClick={() => handlePdfZoom(0.1)}
                      style={pdfButtonStyle(previewPdfScale >= 2.5)}
                      disabled={previewPdfScale >= 2.5}
                    >
                      확대
                    </button>
                    <button
                      onClick={() => setPreviewPdfScale(1)}
                      style={baseStyles.pdfButton}
                    >
                      100%
                    </button>
                  </div>
                </div>
              )}
              <div ref={previewContainerRef} style={previewContainerStyle}>
                {previewLoading && <p>파일을 불러오는 중입니다...</p>}
                {previewError && !previewLoading && <p style={{ color: 'red' }}>{previewError}</p>}
                {!previewLoading && !previewError && previewObjectUrl && (
                  (() => {
                    const lowerName = previewFileName.toLowerCase();
                    if (lowerName.endsWith('.pdf')) {
                      const pdfViewerUrl = `${previewObjectUrl}#toolbar=0&navpanes=0&scrollbar=1&view=Fit`;
                      return (
                        <object
                          data={pdfViewerUrl}
                          type="application/pdf"
                          style={baseStyles.pdfObject}
                        >
                          <embed
                            src={pdfViewerUrl}
                            type="application/pdf"
                            style={baseStyles.pdfObject}
                          />
                        </object>
                      );
                    }
                    return (
                      <img
                        src={previewObjectUrl}
                        style={baseStyles.filePreview}
                        alt="이미지 미리보기"
                      />
                    );
                  })()
                )}
                {!previewLoading && !previewError && !previewObjectUrl && isPdfPreview && previewPdfData && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Document
                      file={previewPdfData}
                      onLoadSuccess={handlePdfDocumentLoad}
                      loading="PDF 로딩 중..."
                      onLoadError={handlePdfDocumentError}
                      error="PDF 로딩 실패"
                      noData="표시할 PDF가 없습니다."
                    >
                      <Page
                        pageNumber={previewPdfPage}
                        width={effectivePdfWidth}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    </Document>
                  </div>
                )}
                {!previewLoading && !previewError && !previewObjectUrl && (!isPdfPreview || !previewPdfData) && (
                  <p>파일을 불러올 수 없습니다.</p>
                )}
              </div>
            </div>
            <div style={baseStyles.modalFooter}>
              {previewTargetApp?.status === '접수중' && (
                <>
                  <button
                    onClick={async () => {
                      if (!window.confirm('이 연차 신청을 승인하시겠습니까?')) return;
                      try {
                        await api.put(`/leave/applications/${previewTargetApp.id}`, { status: '승인', comment: '' });
                        setMessage('연차 신청이 승인되었습니다.');
                        fetchApplications();
                        handleClosePreview();
                      } catch (err) {
                        setError(err.response?.data?.message || '승인 실패');
                      }
                    }}
                    style={{...baseStyles.button, ...baseStyles.approveButton, marginRight: '10px'}}
                  >
                    즉시 승인
                  </button>
                  <button
                    onClick={() => {
                      handleOpenRejectModal(previewTargetApp);
                      handleClosePreview();
                    }}
                    style={{...baseStyles.button, ...baseStyles.rejectButton, marginRight: '10px'}}
                  >
                    반려
                  </button>
                </>
              )}
              {previewFiles.length > 0 && (
                <button
                  onClick={() => {
                    const selectedFile = previewFiles.find(file => file.filename === previewFileName);
                    if (selectedFile) {
                      handleFileDownload(selectedFile.filename, selectedFile.originalname);
                    }
                  }}
                  style={{...baseStyles.button, ...baseStyles.approveButton, marginRight: '10px'}}
                >
                  다운로드
                </button>
              )}
              <button onClick={handleClosePreview} style={baseStyles.button}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 반려 사유 입력 모달 */}
      {isRejectModalOpen && rejectTargetApp && (
        <div style={baseStyles.modalOverlay} onClick={handleCloseRejectModal}>
          <div style={{ ...baseStyles.modal, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={baseStyles.modalHeader}>
              <h3 style={{ margin: 0 }}>연차 신청 반려</h3>
              <button onClick={handleCloseRejectModal} style={baseStyles.button}>×</button>
            </div>
            <div style={baseStyles.modalBody}>
              <p style={{ fontSize: '0.95rem', marginBottom: '12px', color: '#333' }}>
                <strong>{rejectTargetApp.participant?.name}</strong>님의 연차 신청을 반려합니다. 사유를 입력해 주세요.
              </p>
              <textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="반려 사유 입력 (선택)"
                rows={4}
                style={{ width: '100%', padding: '10px', boxSizing: 'border-box', resize: 'vertical', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.95rem' }}
              />
            </div>
            <div style={baseStyles.modalFooter}>
              <button onClick={handleCloseRejectModal} style={baseStyles.button}>취소</button>
              <button
                onClick={handleRejectConfirm}
                style={{ ...baseStyles.button, ...baseStyles.rejectButton }}
              >
                반려
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 승인 설정 저장 확인 모달 */}
      {isConfirmSettingsModalOpen && confirmSettingsPending && (
        <div style={baseStyles.modalOverlay} onClick={handleConfirmSettingsModalCancel}>
          <div style={{ ...baseStyles.modal, maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div style={baseStyles.modalHeader}>
              <h3 style={{ margin: 0 }}>
                {confirmSettingsPending.approvalMode === 'auto' ? '자동승인으로 변경하시겠습니까?' : '수동승인으로 변경하시겠습니까?'}
              </h3>
              <button onClick={handleConfirmSettingsModalCancel} style={baseStyles.button}>×</button>
            </div>
            <div style={baseStyles.modalBody}>
              <p style={{ fontSize: '1rem', lineHeight: 1.6, color: '#333' }}>
                {confirmSettingsPending.approvalMode === 'auto' ? (
                  <>
                    자동승인을 적용하면, 이번 변경 시점 <strong>이후</strong>에 접수되는 연차 신청만 자동으로 승인됩니다.
                    이미 접수된 연차 신청 건은 연차승인 및 취소 메뉴에서 <strong>수동으로 승인</strong>해 주세요.
                  </>
                ) : (
                  <>
                    수동승인을 적용하면, 이후 접수되는 모든 연차 신청은 관리자가 연차승인 및 취소 메뉴에서 <strong>직접 승인</strong>해야 합니다.
                  </>
                )}
              </p>
            </div>
            <div style={baseStyles.modalFooter}>
              <button onClick={handleConfirmSettingsModalCancel} style={baseStyles.button}>취소</button>
              <button
                onClick={handleConfirmSettingsModalConfirm}
                style={{ ...baseStyles.button, ...baseStyles.approveButton }}
                disabled={saveSettingsLoading}
              >
                {saveSettingsLoading ? '저장 중...' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveApprovalPage;